import React, { memo, useMemo, useCallback, useReducer, useState, useRef, useEffect } from 'react';
import { createUseStyles, Modal, Text } from '@Sage/hoi-poi-ui';

import { useWeb5Context } from 'lib/Web5Context';
import { intercomHideAndShow } from 'utils/custom';
import {
    ENTITIES,
    MAX_IMPORT_COLUMNS,
    MAX_IMPORT_ROWS,
    MAX_IMPORT_COLUMNS_PARAM,
} from 'lib/constants';
import { ENTITIES_DATA } from './constants';

import ImportsLists from './ImportsLists';
import ImportStepper from './ImportStepper';
import ExportModal from './ExportModal';
import ImportedModal from './ImportedModal';

import ImportContext from './ImportContext';

import styles from './styles';
const useStyles = createUseStyles(styles, 'AvoidDuplicates');

const reducer = (state, action) => {
    switch (action.type) {
        case 'CHANGE_IMPORT':
            state = { ...state, activeView: action.view, activeImport: action.activeImport };
            return state;
        case 'CHANGE_TAB':
            state = {
                ...state,
                activeTab: action.tab,
                activeShrinkedTab: null,
            };
            return state;
        case 'CHANGE_SHRINKED_TAB':
            state = {
                ...state,
                activeTab: null,
                activeShrinkedTab: action.value,
            };
            return state;
        case 'ON_FINISH':
            return {
                ...state,
                activeView: 'lists',
                activeImport: null,
                modalFinishIsOpen: true,
                isEdit: false,
            };
        case 'CLOSE_MODAL_FINISH':
            return { ...state, modalFinishIsOpen: false, entityName: null };
        case 'CHANGE_MATCH_BY':
            return { ...state, matchBy: action.matchBy };
        case 'CHANGE_ADVANCED_TYPE':
            return { ...state, advancedType: action.advancedType };
        case 'SET_IS_EDIT':
            return { ...state, isEdit: action.isEdit };
        case 'RESET_IS_EDIT':
            return { ...state, isEdit: false };
        default:
            return state;
    }
};

const initialState = {
    activeTab: ENTITIES.ACCOUNTS.importExport,
    activeView: 'lists', // lists | import // FIXME
    activeImport: null,
    modalFinishIsOpen: false,
    matchBy: '',
    advancedType: null,
    isEdit: false,
};

const Imports = ({
    entityLogName = 'import',
    idSetupRole,
    imgImportsName,
    imgImportsFmId,
    imgImportsExtId,
    isNotAdmin,
    contextOverrides,
    activeTab: activeTabProp,
    maxImportColumns = MAX_IMPORT_COLUMNS,
    maxImportRows = MAX_IMPORT_ROWS,
}) => {
    const classes = useStyles();
    const { t, logEvent } = useWeb5Context();
    const ExportModalRef = useRef();
    const ImportedModalRef = useRef();
    const isFirstRender = useRef(true);
    const [state, dispatch] = useReducer(reducer, initialState);
    const [activeRowId, setActiveRowId] = useState(null);
    const importLimit = contextOverrides?.permissions?.importExport?.importLimit;

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            if (activeTabProp) {
                dispatch({ type: 'CHANGE_TAB', tab: activeTabProp });
            }
            logEvent({
                event: entityLogName,
                submodule: activeTabProp || state.activeTab,
                functionality: 'listView',
            });
        }
    }, [entityLogName, logEvent, state.activeTab, activeTabProp]);

    const onChangeView = useCallback(
        (view, activeImport) => {
            // Compatibility between creation and edition
            intercomHideAndShow('hide');
            let item = activeImport;
            if (item) item.file = { name: item.name };
            return dispatch({ type: 'CHANGE_IMPORT', view, activeImport: item });
        },
        [dispatch],
    );

    const onChangeTab = useCallback(
        (tab) => {
            logEvent({
                event: entityLogName,
                submodule: tab,
                functionality: 'listView',
            });
            dispatch({ type: 'CHANGE_TAB', tab });
        },
        [entityLogName, logEvent],
    );

    const onChangeShrinked = useCallback(
        (value) => {
            logEvent({
                event: entityLogName,
                submodule: 'otherTables',
                functionality: 'listView',
            });
            dispatch({ type: 'CHANGE_SHRINKED_TAB', value });
        },
        [entityLogName, logEvent],
    );

    const onCancelImport = useCallback(
        (id) => {
            let action = {
                type: 'CHANGE_IMPORT',
                view: 'lists',
                activeImport: null,
            };
            dispatch(action);
            dispatch({ type: 'RESET_IS_EDIT' });
        },
        [dispatch],
    );

    const setIsEdit = useCallback(() => {
        dispatch({ type: 'SET_IS_EDIT', isEdit: true });
    }, []);

    const onChangeMatchBy = useCallback((value) => {
        dispatch({ type: 'CHANGE_MATCH_BY', matchBy: value });
    }, []);

    const onChangeAdvancedType = useCallback((value) => {
        dispatch({ type: 'CHANGE_ADVANCED_TYPE', advancedType: value });
    }, []);

    const onExport = useCallback((entity, importType, withData, syncNames) => {
        ExportModalRef.current.open(entity, importType, withData, syncNames);
    }, []);

    const onImported = useCallback((id) => {
        setActiveRowId(id);
        dispatch({ type: 'ON_FINISH' });
    }, []);

    const onShowErrors = useCallback((data) => ImportedModalRef.current.open(data), []);

    const onConfirmModalFinish = useCallback(() => dispatch({ type: 'CLOSE_MODAL_FINISH' }), []);

    const entityName = useMemo(
        () => state.activeTab || (state.activeShrinkedTab ? state.activeShrinkedTab.value : null),
        [state.activeShrinkedTab, state.activeTab],
    );

    const entityLabelValue = useMemo(
        () =>
            state.activeTab ||
            (state.activeShrinkedTab ? state.activeShrinkedTab.description : null),
        [state.activeShrinkedTab, state.activeTab],
    );

    const entityLabel = useMemo(
        () => t(ENTITIES_DATA[entityName]?.literal) || entityLabelValue,
        [entityName, entityLabelValue, t],
    );

    return (
        <ImportContext.Provider value={contextOverrides || {}}>
            {state && (
                <ImportsLists
                    entityLogName={entityLogName}
                    entityName={entityName}
                    onChangeView={onChangeView}
                    onChangeTab={onChangeTab}
                    onChangeShrinked={onChangeShrinked}
                    activeShrinkedTab={state.activeShrinkedTab}
                    activeTab={state.activeTab}
                    onExport={onExport}
                    onShowErrors={onShowErrors}
                    idSetupRole={idSetupRole}
                    activeRowId={activeRowId}
                    setActiveRowId={setActiveRowId}
                    isNotAdmin={isNotAdmin}
                    setIsEdit={setIsEdit}
                />
            )}
            {state?.activeView === 'import' && (
                <ImportStepper
                    entityLabel={entityLabel}
                    entityName={entityName}
                    entityLogName={entityLogName}
                    item={state.activeImport}
                    onCancel={onCancelImport}
                    onExport={onExport}
                    onImported={onImported}
                    shrinkedTab={state?.activeShrinkedTab || null}
                    maxImportColumns={importLimit ? maxImportColumns : MAX_IMPORT_COLUMNS_PARAM}
                    maxImportRows={importLimit ? maxImportRows : MAX_IMPORT_ROWS}
                    matchBy={state.matchBy}
                    onChangeMatchBy={onChangeMatchBy}
                    advancedType={state.advancedType}
                    onChangeAdvancedType={onChangeAdvancedType}
                    imgImportsName={imgImportsName}
                    imgImportsFmId={imgImportsFmId}
                    imgImportsExtId={imgImportsExtId}
                    isEdit={state.isEdit}
                    idSetupRole={idSetupRole}
                />
            )}
            <ExportModal
                entityLogName={entityLogName}
                maxImportColumns={importLimit ? maxImportColumns : MAX_IMPORT_COLUMNS_PARAM}
                maxImportRows={importLimit ? maxImportRows : MAX_IMPORT_ROWS}
                shrinkedTab={state?.activeShrinkedTab || null}
                onRef={(ref) => (ExportModalRef.current = ref)}
                matchBy={state.matchBy}
            />
            <ImportedModal
                activeRowId={activeRowId}
                setActiveRowId={setActiveRowId}
                onRef={(ref) => (ImportedModalRef.current = ref)}
            />
            <Modal
                title={t('cfm_label_import_in_progress')}
                size="small"
                isOpen={state.modalFinishIsOpen}
                useCornerClose={false}
                onConfirm={onConfirmModalFinish}
                confirmText={t('action_understood')}
                className={classes.modalFinish}
                classes={{ header: classes.finishHeader, footer: classes.finishFooter }}
            >
                <Text>{t('cfm_label_import_in_progress_desc')}</Text>
            </Modal>
        </ImportContext.Provider>
    );
};

Imports.propTypes = {};

export default memo(Imports);
